import { useNotificationStore } from '~/stores/notifications'
import { useAuthStore } from '~/stores/auth'

export const useNotifications = (options = {}) => {
    const {
        autoStart = true,
        interval = 30000,
        entities = ['users', 'roles', 'contactRequests'],
        onNotificationReceived = null
    } = options

    const notificationStore = useNotificationStore()
    const authStore = useAuthStore()
    const route = useRoute()

    // Ref reactive pour le statut
    const isEnabled = ref(false)

    // Fonction pour démarrer les notifications
    const startNotificationCheck = () => {
        if (authStore.isAuthenticated && !isEnabled.value) {
            notificationStore.startPeriodicCheck(interval)
            isEnabled.value = true
            console.log('🔔 Notifications démarrées')
        }
    }

    // Fonction pour arrêter les notifications
    const stopNotificationCheck = () => {
        if (isEnabled.value) {
            notificationStore.stopPeriodicCheck()
            isEnabled.value = false
            console.log('🔕 Notifications arrêtées')
        }
    }

    // Fonction pour forcer une vérification
    const forceCheck = async () => {
        if (authStore.isAuthenticated) {
            await notificationStore.checkForUpdates()
        }
    }

    // Marquer une entité comme synchronisée automatiquement
    const autoMarkSynced = async (entityName) => {
        if (entities.includes(entityName)) {
            await notificationStore.markEntityAsSynced(entityName)
        }
    }

    // Watcher pour l'authentification
    watch(() => authStore.isAuthenticated, (isAuth) => {
        if (isAuth && autoStart) {
            nextTick(() => {
                startNotificationCheck()
            })
        } else {
            stopNotificationCheck()
        }
    }, { immediate: true })

    // Watcher pour détecter de nouvelles notifications
    watch(() => notificationStore.totalNotifications, (newCount, oldCount) => {
        if (newCount > (oldCount || 0) && onNotificationReceived) {
            onNotificationReceived(newCount - (oldCount || 0))
        }
    })

    // Auto-sync basé sur la route
    watch(() => route.path, (newPath) => {
        const routeEntityMap = {
            '/admin/users': 'users',
            '/admin/roles': 'roles',
            '/admin/contact-requests': 'contactRequests'
        }

        const entity = routeEntityMap[newPath]
        if (entity) {
            // Attendre un peu puis marquer comme synchronisé
            setTimeout(() => {
                autoMarkSynced(entity)
            }, 1000)
        }
    })

    // Cleanup
    onUnmounted(() => {
        stopNotificationCheck()
    })

    return {
        // State du store
        ...storeToRefs(notificationStore),

        // Actions du store
        checkForUpdates: notificationStore.checkForUpdates,
        getNotificationDetails: notificationStore.getNotificationDetails,
        markEntityAsSynced: notificationStore.markEntityAsSynced,
        markAllAsSynced: notificationStore.markAllAsSynced,
        resetNotifications: notificationStore.resetNotifications,

        // Contrôles locaux
        isEnabled: readonly(isEnabled),
        startNotificationCheck,
        stopNotificationCheck,
        forceCheck,
        autoMarkSynced
    }
}